'use client';

import { startTransition, useActionState, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { CeweAddress, CeweAddressComparison } from 'services';
import { useCartAddressFormStore } from 'stores/cart';
import { BILLING_FIELD_NAME_PREFIX } from '../../../constants';
import { saveAddressAction } from '../../../../actions/address/saveAddressAction';
import { scrollToError } from '../../../utils/scrollToError';
import { trackSubmitDone, trackSubmitFail } from '../../../../analytics/address';

interface UseCartAddressStepSaveOptions {
  constants: Record<string, string>;
  paymentRoute: string;
  formRef: { current: HTMLFormElement | null };
}

interface CartAddressStepSaveController {
  formAction: (payload: FormData) => void;
  handleSubmit: () => void;
  isPending: boolean;
  ceweDialogOpen: boolean;
  ceweSuggestion?: CeweAddressComparison;
  closeCeweDialog: () => void;
  submitWithAddress: (form: HTMLFormElement, address: CeweAddress) => void;
}

function getVisibleMobileBillingSection(form: HTMLFormElement) {
  const mobileSection = form.querySelector<HTMLElement>('[data-cart-address-mobile-billing]');
  const checkbox = mobileSection?.querySelector<HTMLInputElement>('input[name="billingMatchesShippingMobileCheckbox"]');

  if (!checkbox || !mobileSection || window.getComputedStyle(mobileSection).display === 'none') {
    return null;
  }

  return mobileSection;
}

/** Coordinates shipping-address persistence, validation state, and payment navigation. */
export function useCartAddressStepSave({
  constants,
  paymentRoute,
  formRef
}: UseCartAddressStepSaveOptions): CartAddressStepSaveController {
  const router = useRouter();
  const setValidationErrors = useCartAddressFormStore(state => state.setValidationErrors);
  const setFormData = useCartAddressFormStore(state => state.setFormData);
  const [state, action, isPending] = useActionState(saveAddressAction.bind(null, constants), null);
  const hasSubmitted = useRef(false);
  const [ceweDialogOpen, setCeweDialogOpen] = useState(false);
  const ceweSuggestion = state && 'ceweSuggestion' in state ? state.ceweSuggestion : undefined;

  // Apply server-side validation errors and scroll to the first relevant field.
  useEffect(() => {
    if (state && !('ceweSuggestion' in state)) {
      trackSubmitFail();
      const currentErrors = useCartAddressFormStore.getState();
      setValidationErrors(
        { ...currentErrors.fieldErrors, ...state.fieldErrors },
        { ...currentErrors.billingFieldErrors, ...state.billingFieldErrors }
      );
    } else {
      setValidationErrors({}, {});
    }

    if (state && !('ceweSuggestion' in state)) {
      const form = formRef.current;

      if (form) {
        const hasShippingError = Boolean(scrollToError(form, state.fieldErrors));

        if (!hasShippingError && Object.keys(state.billingFieldErrors ?? {}).length > 0) {
          const mobileBillingSection = getVisibleMobileBillingSection(form);

          if (mobileBillingSection) {
            mobileBillingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            scrollToError(form, state.billingFieldErrors, BILLING_FIELD_NAME_PREFIX);
          }
        }
      }
    }
  }, [formRef, setValidationErrors, state]);

  // A CEWE correction is returned as action state; show it for user confirmation.
  useEffect(() => {
    if (ceweSuggestion) {
      setCeweDialogOpen(true);
    }
  }, [ceweSuggestion]);

  // A null result after a pending save means the address was accepted; continue to payment.
  useEffect(() => {
    if (hasSubmitted.current && !isPending && state === null) {
      trackSubmitDone();
      router.push(paymentRoute);
      hasSubmitted.current = false;
    }
  }, [isPending, paymentRoute, router, state]);

  const handleSubmit = () => {
    hasSubmitted.current = true;
  };

  const submitWithAddress = (form: HTMLFormElement, address: CeweAddress) => {
    // Keep the form store in sync with the selected address before resubmitting it.
    const { destinationType, formData: storedFormData } = useCartAddressFormStore.getState();
    const currentShippingFormData = storedFormData.shipping[destinationType];

    if (currentShippingFormData) {
      setFormData('shipping', destinationType, {
        ...currentShippingFormData,
        firstName: address.firstName,
        lastName: address.lastName,
        street: address.street,
        zip: address.zip,
        city: address.city,
        country: address.country.toUpperCase()
      });
    }

    const formData = new FormData(form);
    formData.set('street', address.street);
    formData.set('houseNumber', '');
    formData.set('postcode', address.zip);
    formData.set('city', address.city);
    formData.set('skipCeweAddressValidation', 'true');
    hasSubmitted.current = true;
    setCeweDialogOpen(false);

    // Skip a duplicate CEWE check for this confirmed address and track the save as pending.
    startTransition(() => action(formData));
  };

  return {
    formAction: action,
    handleSubmit,
    isPending,
    ceweDialogOpen,
    ceweSuggestion,
    closeCeweDialog: () => setCeweDialogOpen(false),
    submitWithAddress: (form, address) => submitWithAddress(form, address)
  };
}
